import { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import './Admin.css';

function authFetch(url, token, options = {}) {
  return fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...options.headers },
  });
}

function RoleBadge({ role }) {
  return (
    <span className={`badge ${role === 'admin' ? 'badge-warning' : 'badge-info'}`}>
      {role}
    </span>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function UserManagement({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [roleChanging, setRoleChanging] = useState({});

  useEffect(() => {
    async function load() {
      try {
        const res = await authFetch('/api/admin/users', token);
        if (res.ok) {
          const data = await res.json();
          setUsers(data.users || data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  async function handleRoleChange(user) {
    const newRole = user.role === 'admin' ? 'learner' : 'admin';
    setRoleChanging(r => ({ ...r, [user.id]: true }));
    try {
      const res = await authFetch(`/api/admin/users/${user.id}/role`, token, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole }),
      });
      if (!res.ok) throw new Error('Failed to change role');
      setUsers(u => u.map(x => x.id === user.id ? { ...x, role: newRole } : x));
    } catch (err) {
      setError(err.message);
    } finally {
      setRoleChanging(r => ({ ...r, [user.id]: false }));
    }
  }

  async function handleDelete(user) {
    try {
      const res = await authFetch(`/api/admin/users/${user.id}`, token, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete user');
      setUsers(u => u.filter(x => x.id !== user.id));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteConfirm(null);
    }
  }

  if (loading) return <div className="admin-section"><p style={{ color: 'var(--text-tertiary)' }}>Loading...</p></div>;

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <div>
          <h2 className="admin-section-title">Users</h2>
          <p className="admin-section-desc">{users.length} user{users.length !== 1 ? 's' : ''} total</p>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {users.length === 0 ? (
        <div className="empty-admin"><p>No users found.</p></div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Joined</th>
              <th>Groups</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td className="user-username">{user.username}</td>
                <td style={{ color: 'var(--text-secondary)' }}>{user.email || '—'}</td>
                <td><RoleBadge role={user.role} /></td>
                <td style={{ color: 'var(--text-secondary)' }}>{formatDate(user.createdAt || user.created_at)}</td>
                <td style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                  {(user.groups || []).map(g => g.name || g).join(', ') || '—'}
                </td>
                <td>
                  <div className="action-btn-group">
                    <button
                      className="action-btn"
                      title={user.role === 'admin' ? 'Change to Learner' : 'Change to Admin'}
                      onClick={() => handleRoleChange(user)}
                      disabled={roleChanging[user.id]}
                    >
                      {roleChanging[user.id] ? '…' : user.role === 'admin' ? '↓' : '↑'}
                    </button>
                    <button
                      className="action-btn action-btn-danger"
                      title="Delete user"
                      onClick={() => setDeleteConfirm(user)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {deleteConfirm && (
        <div className="admin-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete User</h3>
            <p>
              Are you sure you want to delete <strong>{deleteConfirm.username}</strong>?
              All their data will be permanently removed.
            </p>
            <div className="admin-modal-actions">
              <button className="btn-sm btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn-sm btn-danger" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
